import { getFallbackBundle, loadBundle } from '@jumpstone/i18n/wagepro';
import type { AppLocale, Bundle } from '@jumpstone/i18n/wagepro';

/** WagePro supports en/si/ta for the runtime locale switcher. */
export type WageProLocale = Extract<AppLocale, 'en-001' | 'si-LK' | 'ta-LK'>;
export const WAGEPRO_LOCALES: readonly WageProLocale[] = [
  'en-001',
  'si-LK',
  'ta-LK',
] as const;

export interface I18nState {
  locale: WageProLocale;
  bundle: Bundle;
  fallbackBundle: Bundle;
}

function detectLocale(): WageProLocale {
  const stored =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('wagepro-locale')
      : null;
  if (stored === 'si-LK' || stored === 'ta-LK') return stored;

  const lang =
    typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : 'en';
  if (lang.startsWith('si')) return 'si-LK';
  if (lang.startsWith('ta')) return 'ta-LK';
  return 'en-001';
}

/**
 * Loads the bundle for the given WagePro locale.
 * Always resolves — falls back to en-001 if load fails.
 */
export async function loadWageProBundle(
  locale: WageProLocale,
): Promise<Bundle> {
  const fallback = getFallbackBundle();
  if (locale === 'en-001') return fallback;
  try {
    return await loadBundle(locale);
  } catch {
    console.error(
      `[wagepro-i18n] Failed to load bundle for ${locale}, falling back to en-001`,
    );
    return fallback;
  }
}

/** Detects the active locale and loads the appropriate translation bundle. */
export async function initI18n(): Promise<I18nState> {
  const fallbackBundle = getFallbackBundle();
  const locale = detectLocale();
  const bundle = await loadWageProBundle(locale);
  return { locale, bundle, fallbackBundle };
}

/** Persist the user's locale choice and return the new bundle. */
export async function switchLocale(next: WageProLocale): Promise<I18nState> {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('wagepro-locale', next);
  }
  const fallbackBundle = getFallbackBundle();
  const bundle = await loadWageProBundle(next);
  return { locale: next, bundle, fallbackBundle };
}

/**
 * Build a locale-aware Intl.NumberFormat for monetary amounts.
 * Always uses the active locale — never hardcodes en-US.
 */
export function buildCurrencyFormatter(
  locale: AppLocale,
  currency: string,
): Intl.NumberFormat {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a minor-unit integer as a currency string using the active locale.
 * Money is stored as integer minor units per docs/money-rules.md.
 */
export function formatMoney(
  minorUnits: number,
  currency: string,
  locale: AppLocale,
): string {
  const fmt = buildCurrencyFormatter(locale, currency);
  return fmt.format(minorUnits / 100);
}
